import { ReactNode, useCallback, useRef, useState } from "react";
import { AlertTriangle, CheckCircle2, Info, Trash2, X } from "lucide-react";

type ConfirmTone = "danger" | "warning" | "info" | "success";

type ConfirmOptions = {
  title: string;
  description?: ReactNode;
  confirmText?: string;
  cancelText?: string;
  tone?: ConfirmTone;
};

type ConfirmState = Required<Omit<ConfirmOptions, "description">> & {
  description?: ReactNode;
};

const toneConfig: Record<
  ConfirmTone,
  {
    icon: ReactNode;
    iconClass: string;
    confirmClass: string;
  }
> = {
  danger: {
    icon: <Trash2 size={22} />,
    iconClass: "bg-[#fdebea] text-[#9a2d2d]",
    confirmClass: "bg-[#9a2d2d] text-white hover:bg-[#7f2323]",
  },
  warning: {
    icon: <AlertTriangle size={22} />,
    iconClass: "bg-[#fff6df] text-[#7a5610]",
    confirmClass: "bg-[#7a5610] text-white hover:bg-[#654407]",
  },
  info: {
    icon: <Info size={22} />,
    iconClass: "bg-[#eef3f8] text-[#255071]",
    confirmClass: "bg-[#315746] text-white hover:bg-[#203d32]",
  },
  success: {
    icon: <CheckCircle2 size={22} />,
    iconClass: "bg-[#e9f4ec] text-[#245438]",
    confirmClass: "bg-[#315746] text-white hover:bg-[#203d32]",
  },
};

export function useConfirmDialog() {
  const resolverRef = useRef<((confirmed: boolean) => void) | null>(null);
  const [state, setState] = useState<ConfirmState | null>(null);

  const close = useCallback((confirmed: boolean) => {
    resolverRef.current?.(confirmed);
    resolverRef.current = null;
    setState(null);
  }, []);

  const confirm = useCallback((options: ConfirmOptions) => {
    setState({
      title: options.title,
      description: options.description,
      confirmText: options.confirmText || "確認",
      cancelText: options.cancelText || "取消",
      tone: options.tone || "danger",
    });

    return new Promise<boolean>((resolve) => {
      resolverRef.current = resolve;
    });
  }, []);

  const ConfirmDialog = useCallback(() => {
    if (!state) return null;

    const tone = toneConfig[state.tone];

    return (
      <div className="fixed inset-0 z-[70] flex items-end justify-center bg-[#10251c]/40 px-0 py-0 sm:items-center sm:px-4 sm:py-6">
        <button aria-label="取消操作" className="absolute inset-0 cursor-default" onClick={() => close(false)} type="button" />

        <section className="relative w-full max-w-[460px] rounded-t-lg border border-[#dfe6e1] bg-white shadow-[0_22px_70px_rgba(32,61,50,.2)] sm:rounded-md">
          <div className="flex items-start gap-4 px-5 py-5">
            <div className={`flex h-12 w-12 shrink-0 items-center justify-center rounded-md ${tone.iconClass}`}>{tone.icon}</div>

            <div className="min-w-0 flex-1">
              <div className="flex items-start justify-between gap-3">
                <h2 className="text-xl font-bold leading-7 text-[#203d32]">{state.title}</h2>
                <button
                  aria-label="關閉"
                  className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-md border border-[#d8e0db] text-[#315746] transition hover:bg-[#f0f5f2]"
                  onClick={() => close(false)}
                  type="button"
                >
                  <X size={18} />
                </button>
              </div>

              {state.description && <div className="mt-3 text-sm leading-6 text-[#59665f]">{state.description}</div>}
            </div>
          </div>

          <div className="flex flex-col-reverse gap-3 border-t border-[#edf0ee] px-5 py-4 sm:flex-row sm:justify-end">
            <button
              className="h-10 rounded-md border border-[#cfd8d2] bg-white px-4 font-bold text-[#315746] transition hover:bg-[#f0f5f2]"
              onClick={() => close(false)}
              type="button"
            >
              {state.cancelText}
            </button>
            <button className={`h-10 rounded-md px-5 font-bold transition ${tone.confirmClass}`} onClick={() => close(true)} type="button">
              {state.confirmText}
            </button>
          </div>
        </section>
      </div>
    );
  }, [close, state]);

  return { confirm, ConfirmDialog };
}
